import { memo, useMemo, useState } from "react";
import { useLogoStore, type CustomIcon, type MyTemplate } from "../../state/logoStore";
import { renderLogoSVG } from "../../engine/render";
import { templateToRenderState } from "../../engine/templateToRenderState";
import { generateIdeas, ideaToStatePatch, type DesignIdea } from "../../engine/ideaGenerator";
import { gradeClass } from "./EditorControls";

/**
 * Saved-design card — memoized so editing the title/slogan/etc. in the live
 * editor doesn't re-render every saved-design preview on each keystroke.
 * The expensive part is renderLogoSVG, which runs only when the underlying
 * template OR the customIcons array changes.
 */
const SavedDesignCard = memo(function SavedDesignCard({
  t, customIcons, onLoad, onDelete,
}: {
  t: MyTemplate;
  customIcons: CustomIcon[];
  onLoad: (id: string) => void;
  onDelete: (t: MyTemplate) => void;
}) {
  const svg = useMemo(
    () => renderLogoSVG(
      templateToRenderState(t, "Your Brand", "tagline"),
      { width: 500, height: 500, customIcons },
    ),
    [t, customIcons],
  );
  return (
    <div className="my-design-card">
      <button
        type="button"
        className="my-design-btn"
        onClick={() => onLoad(t.id)}
        title={`Load "${t.name}" — saved ${new Date(t.savedAt).toLocaleDateString()}`}
      >
        <div className="my-design-preview" dangerouslySetInnerHTML={{ __html: svg }} />
        <div className="my-design-name">{t.name}</div>
      </button>
      <button
        type="button"
        className="my-design-delete"
        title="Delete"
        onClick={(e) => { e.stopPropagation(); onDelete(t); }}
      >
        ✕
      </button>
    </div>
  );
});

/**
 * In-editor "Suggested" tab content. Two stacked sections:
 *   1. My saved designs   — single-column gallery of MyTemplate entries
 *   2. 20 design ideas    — auto-generated icon × layout × palette × pattern grid
 */
export function IdeasPanel() {
  const s = useLogoStore();
  const [seed, setSeed] = useState(0);
  const ideas = useMemo(() => generateIdeas({
    title: s.title,
    slogan: s.slogan,
    referencePalette: s.referencePalette,
    customIcons: s.customIcons,
    preferIconId: s.iconId,
    count: 20,
    seed,
  }), [s.title, s.slogan, s.referencePalette, s.customIcons, s.iconId, seed]);

  function applyIdea(idea: DesignIdea) {
    s.pushHistory();
    s.setStyle(ideaToStatePatch(idea) as Parameters<typeof s.setStyle>[0]);
    s.setIcon(idea.iconId);
  }

  return (
    <>
      {s.myTemplates.length > 0 && (
        <>
          <div className="ideas-head">
            <div>
              <div className="ideas-title">My saved designs</div>
              <div className="ideas-sub">{s.myTemplates.length} saved · click to load · ✕ to delete</div>
            </div>
          </div>
          <div className="my-designs-grid">
            {s.myTemplates.map((t) => (
              <SavedDesignCard
                key={t.id}
                t={t}
                customIcons={s.customIcons}
                onLoad={s.loadMyTemplate}
                onDelete={(tpl) => {
                  if (confirm(`Delete "${tpl.name}"? This can't be undone.`)) {
                    s.deleteMyTemplate(tpl.id);
                  }
                }}
              />
            ))}
          </div>
        </>
      )}

      <div className="ideas-head">
        <div>
          <div className="ideas-title">{ideas.length} design ideas</div>
          <div className="ideas-sub">Mixes icons · layouts · fonts · containers · patterns · {s.referencePalette.length > 0 ? "your palette" : "curated palettes"} · scored &amp; ranked</div>
        </div>
        <button className="btn ghost btn-sm" onClick={() => setSeed((v) => v + 1)} title="Reshuffle">↻</button>
      </div>

      <div className="ideas-grid">
        {ideas.map((idea, i) => {
          const svg = renderLogoSVG(
            { title: s.title || "Your Brand", slogan: s.slogan || "tagline", ...idea, qrText: null } as Parameters<typeof renderLogoSVG>[0],
            { width: 240, height: 240, customIcons: s.customIcons }
          );
          const grade = idea.score.grade;
          const isCurrentMatch =
            s.iconId === idea.iconId &&
            s.layout === idea.layout &&
            s.background === idea.background;
          return (
            <button
              key={i}
              className={`idea-card ${isCurrentMatch ? "active" : ""}`}
              onClick={() => applyIdea(idea)}
              title={`Click to apply · score ${idea.score.scorePercent}/100 (${grade})`}
            >
              <div className="idea-svg" dangerouslySetInnerHTML={{ __html: svg }} />
              <div className={`idea-grade grade-${gradeClass(grade)}`}>{grade}<span> {idea.score.scorePercent}</span></div>
            </button>
          );
        })}
      </div>

      {ideas.length === 0 && (
        <div style={{ padding: "20px 0", fontSize: 12, color: "#94a3b8", textAlign: "center" }}>
          Add a company name to generate ideas.
        </div>
      )}
    </>
  );
}
